import { useMemo, useState } from "react";
import {
  ArrowLeft, Image as ImageIcon, Mic, MessageCircle, MoreVertical, Paperclip,
  Search, Send, Smile, Sparkles, UserCog,
} from "lucide-react";
import { AppShell } from "@/shared/layouts/AppShell.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Spinner } from "@/shared/ui/spinner.jsx";
import { cn } from "@/shared/lib/cn.js";
import { initials, formatMessageTime } from "@/shared/lib/format.js";
import { toast } from "@/shared/ui/toaster.jsx";
import { MessageBubble } from "@/features/leads/components/LeadChatTab.jsx";
import {
  useConversations, useLeadMessages, useLeads, useSendMessage,
} from "@/features/leads/use-leads.js";
import { useMetaConversations, useSendMetaMessage, useWhatsappConfig } from "./use-whatsapp.js";
import { temperatureLabel } from "@/features/leads/lead-constants.js";

/**
 * Central de atendimento.
 *
 * Junta duas fontes numa inbox só: os leads do CRM e os contatos que
 * chegaram pelo número oficial da Meta. A lógica de agrupar e contar não
 * lidas ficou no servidor — a tela antiga fazia isso no navegador,
 * percorrendo a tabela inteira de logs a cada renderização.
 */

const FILTROS = [
  { id: "todas", label: "Todas" },
  { id: "nao-lidas", label: "Não lidas" },
  { id: "minhas", label: "Minhas" },
];

export function WhatsAppPage() {
  const { data: leads = [], isLoading: carregandoLeads } = useLeads();
  const { data: resumoLeads = {} } = useConversations();
  const { data: conversasMeta = [] } = useMetaConversations();
  const { data: config } = useWhatsappConfig();

  const enviarLead = useSendMessage();
  const enviarMeta = useSendMetaMessage();

  const [ativa, setAtiva] = useState(null);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [rascunho, setRascunho] = useState("");

  // ------------------------------------------------------------- listas
  const listaMeta = useMemo(() => {
    let itens = conversasMeta;
    if (busca.trim()) itens = itens.filter((c) => c.number.includes(busca.trim()));
    if (filtro === "nao-lidas") itens = itens.filter((c) => c.unread > 0);
    return itens;
  }, [conversasMeta, busca, filtro]);

  const listaLeads = useMemo(() => {
    let itens = leads;
    if (busca.trim()) {
      const q = busca.toLowerCase();
      itens = itens.filter(
        (l) => l.name.toLowerCase().includes(q) || (l.phone ?? "").includes(busca),
      );
    }
    if (filtro === "nao-lidas") itens = itens.filter((l) => (resumoLeads[l.id]?.unread ?? 0) > 0);
    return [...itens].sort((a, b) =>
      String(resumoLeads[b.id]?.lastAt ?? "").localeCompare(String(resumoLeads[a.id]?.lastAt ?? "")),
    );
  }, [leads, resumoLeads, busca, filtro]);

  // ------------------------------------------------------ conversa ativa
  const idAtivo = ativa ?? listaMeta[0]?.id ?? listaLeads[0]?.id ?? null;
  const ehMeta = Boolean(idAtivo?.startsWith?.("meta:"));

  const conversaMeta = ehMeta ? conversasMeta.find((c) => c.id === idAtivo) : null;
  const lead = !ehMeta ? leads.find((l) => l.id === idAtivo) : null;

  const { data: mensagensLead = [], isLoading: carregandoMensagens } = useLeadMessages(
    ehMeta ? null : idAtivo,
  );
  const mensagens = ehMeta ? (conversaMeta?.messages ?? []) : mensagensLead;

  const enviando = enviarLead.isPending || enviarMeta.isPending;

  function enviar() {
    const texto = rascunho.trim();
    if (!texto || !idAtivo) return;

    if (ehMeta) {
      if (!config?.connected) {
        toast.error("Conecte o WhatsApp Meta em Integrações antes de responder.");
        return;
      }
      setRascunho("");
      enviarMeta.mutate(
        { to: conversaMeta.number, message: texto },
        { onError: () => setRascunho(texto) },
      );
      return;
    }

    setRascunho("");
    enviarLead.mutate({ leadId: idAtivo, text: texto }, { onError: () => setRascunho(texto) });
  }

  return (
    <AppShell title="WhatsApp" subtitle="Central de atendimento em tempo real">
      <div className="flex h-[calc(100vh-4rem)]">
        {/* ------------------------------------------------------- inbox */}
        <aside
          className={cn(
            "w-full shrink-0 flex-col border-r border-border/60 bg-card/30 md:flex md:w-80",
            ativa ? "hidden md:flex" : "flex",
          )}
        >
          <div className="border-b border-border/60 p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar conversas..."
                className="h-9 pl-9"
                aria-label="Buscar conversas"
              />
            </div>

            <div className="mt-3 flex gap-1.5">
              {FILTROS.map((f) => (
                <Badge
                  key={f.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setFiltro(f.id)}
                  onKeyDown={(e) => e.key === "Enter" && setFiltro(f.id)}
                  variant={filtro === f.id ? "default" : "outline"}
                  className="cursor-pointer"
                >
                  {f.label}
                </Badge>
              ))}
            </div>
          </div>

          <div className="scrollbar-slim flex-1 overflow-y-auto">
            {carregandoLeads && listaMeta.length === 0 ? (
              <div className="flex h-40 items-center justify-center">
                <Spinner className="h-5 w-5 text-muted-foreground" />
              </div>
            ) : listaMeta.length === 0 && listaLeads.length === 0 ? (
              <p className="p-6 text-center text-xs text-muted-foreground">
                Nenhuma conversa encontrada.
              </p>
            ) : (
              <>
                {listaMeta.length > 0 && <GrupoTitulo>WhatsApp Meta</GrupoTitulo>}
                {listaMeta.map((c) => (
                  <ItemConversa
                    key={c.id}
                    ativo={c.id === idAtivo}
                    titulo={`+${c.number}`}
                    subtitulo={c.lastMessage}
                    hora={formatMessageTime(c.lastAt)}
                    naoLidas={c.unread}
                    iniciais={c.number.slice(-2)}
                    onClick={() => setAtiva(c.id)}
                  />
                ))}

                {listaLeads.length > 0 && <GrupoTitulo>Leads</GrupoTitulo>}
                {listaLeads.map((l) => {
                  const resumo = resumoLeads[l.id];
                  return (
                    <ItemConversa
                      key={l.id}
                      ativo={l.id === idAtivo}
                      titulo={l.name}
                      subtitulo={resumo?.lastMessage ?? "Sem mensagens ainda"}
                      hora={resumo ? formatMessageTime(resumo.lastAt) : ""}
                      naoLidas={resumo?.unread ?? 0}
                      iniciais={initials(l.name)}
                      onClick={() => setAtiva(l.id)}
                    />
                  );
                })}
              </>
            )}
          </div>
        </aside>

        {/* -------------------------------------------------- conversa */}
        <section className={cn("min-w-0 flex-1 flex-col", ativa ? "flex" : "hidden md:flex")}>
          {lead || conversaMeta ? (
            <>
              <div className="flex items-center gap-3 border-b border-border/60 bg-card/30 px-5 py-3">
                <Button
                  size="icon"
                  variant="ghost"
                  className="shrink-0 md:hidden"
                  onClick={() => setAtiva(null)}
                  aria-label="Voltar para conversas"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>

                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-chart-2/20 text-xs font-semibold">
                  {lead ? initials(lead.name) : conversaMeta.number.slice(-2)}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {lead ? lead.name : `+${conversaMeta.number}`}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {lead
                      ? [lead.phone, lead.origin, lead.procedure].filter(Boolean).join(" · ")
                      : "Contato via WhatsApp Meta"}
                  </p>
                </div>

                <Button size="sm" variant="outline" className="hidden gap-1.5 sm:inline-flex">
                  <UserCog className="h-3.5 w-3.5" />
                  Transferir
                </Button>
                <Button size="sm" variant="outline" className="hidden gap-1.5 sm:inline-flex">
                  <Sparkles className="h-3.5 w-3.5" />
                  Automação
                </Button>
                <Button size="icon" variant="ghost" aria-label="Mais opções">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </div>

              <div className="scrollbar-slim flex-1 space-y-3 overflow-y-auto bg-[radial-gradient(circle_at_top,_var(--color-muted)_0,_transparent_50%)] p-6">
                {carregandoMensagens ? (
                  <div className="flex h-full items-center justify-center">
                    <Spinner className="h-6 w-6 text-muted-foreground" />
                  </div>
                ) : mensagens.length === 0 ? (
                  <p className="text-center text-xs text-muted-foreground">
                    Nenhuma conversa ainda. Envie a primeira mensagem.
                  </p>
                ) : (
                  mensagens.map((m) => <MessageBubble key={m.id} message={m} />)
                )}
              </div>

              <div className="border-t border-border/60 bg-card/30 p-3">
                <div className="flex items-end gap-2 rounded-2xl border border-border bg-background p-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Emoji">
                    <Smile className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Anexar arquivo">
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Enviar imagem">
                    <ImageIcon className="h-4 w-4" />
                  </Button>

                  <Input
                    value={rascunho}
                    onChange={(e) => setRascunho(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && enviar()}
                    placeholder="Digite uma mensagem..."
                    maxLength={1000}
                    aria-label="Mensagem"
                    className="h-9 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
                  />

                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Gravar áudio">
                    <Mic className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={enviar}
                    disabled={enviando || !rascunho.trim()}
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Enviar mensagem"
                  >
                    {enviando ? <Spinner /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-muted-foreground">
              <MessageCircle className="h-10 w-10 opacity-40" />
              <p className="text-sm">Selecione uma conversa para começar.</p>
            </div>
          )}
        </section>

        {/* ---------------------------------------------- painel do lead */}
        {lead && (
          <aside className="hidden w-72 shrink-0 flex-col border-l border-border/60 bg-card/30 lg:flex">
            <div className="border-b border-border/60 p-4 text-center">
              <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-chart-2/20 text-lg font-semibold">
                {initials(lead.name)}
              </div>
              <p className="text-sm font-semibold">{lead.name}</p>
              <p className="text-xs text-muted-foreground">{lead.phone}</p>
              <Badge className="mt-2" variant="secondary">
                {lead.temperature === "hot" ? "🔥 " : ""}
                {temperatureLabel(lead.temperature)}
              </Badge>
            </div>

            <div className="space-y-4 p-4 text-xs">
              <Campo label="Procedimento" valor={lead.procedure} />
              <Campo label="Origem" valor={lead.origin} />
              <Campo label="Campanha" valor={lead.campaign} />
              <Campo label="Responsável" valor={lead.owner} />
              <Campo label="Etapa" valor={lead.status} />
            </div>
          </aside>
        )}
      </div>
    </AppShell>
  );
}

function GrupoTitulo({ children }) {
  return (
    <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

function ItemConversa({ ativo, titulo, subtitulo, hora, naoLidas, iniciais, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={ativo ? "true" : undefined}
      className={cn(
        "flex w-full items-start gap-3 border-b border-l-2 border-border/40 p-3 text-left transition-colors",
        "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        ativo ? "border-l-primary bg-muted/60" : "border-l-transparent",
      )}
    >
      <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-chart-2/20 text-xs font-semibold">
        {iniciais}
        <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-success ring-2 ring-card" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-medium">{titulo}</p>
          <span className="shrink-0 text-[10px] text-muted-foreground">{hora}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-xs text-muted-foreground">{subtitulo}</p>
          {naoLidas > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {naoLidas}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function Campo({ label, valor }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 text-sm">{valor || "—"}</p>
    </div>
  );
}
